import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, Loader2, PlugZap, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { AlertsPanel } from "./alerts";
import {
	type AiSettings,
	chatSearch,
	formatDateTime,
	helpdeskApi,
	type MailboxSettings,
	type OtrsSettings,
	type ReportSettings,
	type SettingsSection,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";
import { ResendButton } from "./badges";
import { ReportSubscription } from "./daily-report";
import { BlockRulesPanel } from "./spam";

export const SETTINGS_TABS = ["skrzynka", "ai", "otrs", "spam", "sms", "raport"] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

type FieldType = "text" | "time" | "number" | "bool" | "secret" | "secret-multiline" | "select";

interface Field<T> {
	key: keyof T & string;
	label: string;
	type: FieldType;
	help?: string;
	options?: { value: string; label: string }[];
}

const SECURITY_OPTIONS = [
	{ value: "tls", label: "TLS" },
	{ value: "starttls", label: "STARTTLS" },
	{ value: "plain", label: "bez szyfrowania (tylko test)" },
];

const MAILBOX_FIELDS: Field<MailboxSettings>[] = [
	{
		key: "enabled",
		label: "Odbiór i wysyłka poczty",
		type: "bool",
		help: "Domyślnie wyłączone: dopóki działa OTRS, Farmaster niczego nie pobiera ani nie wysyła. Włącz dopiero przy przejściu.",
	},
	{ key: "imapHost", label: "Serwer IMAP", type: "text" },
	{ key: "imapPort", label: "Port IMAP", type: "number" },
	{ key: "imapSecurity", label: "Szyfrowanie IMAP", type: "select", options: SECURITY_OPTIONS },
	{ key: "folder", label: "Folder", type: "text" },
	{ key: "smtpHost", label: "Serwer SMTP", type: "text" },
	{ key: "smtpPort", label: "Port SMTP", type: "number" },
	{ key: "smtpSecurity", label: "Szyfrowanie SMTP", type: "select", options: SECURITY_OPTIONS },
	{ key: "username", label: "Użytkownik", type: "text" },
	{ key: "password", label: "Hasło", type: "secret" },
	{ key: "fromAddress", label: "Adres nadawcy", type: "text" },
	{ key: "fromName", label: "Nazwa nadawcy", type: "text" },
	{ key: "pollSeconds", label: "Odbiór co [s]", type: "number" },
	{
		key: "cleanupEnabled",
		label: "Sprzątanie skrzynki",
		type: "bool",
		help: "Usuwa z serwera maile już zapisane w Farmasterze i starsze niż retencja.",
	},
	{ key: "retentionDays", label: "Retencja na serwerze [dni]", type: "number" },
	{
		key: "spamDays",
		label: "Kwarantanna spamu [dni]",
		type: "number",
		help: "Po tylu dniach zgłoszenia z widoku Spam są usuwane (z wiadomościami).",
	},
];

const AI_FIELDS: Field<AiSettings>[] = [
	{
		key: "enabled",
		label: "AI włączone",
		type: "bool",
		help: "Klasyfikacja, szkice odpowiedzi i streszczenia. AI niczego nie wysyła.",
	},
	{ key: "baseUrl", label: "Adres CLIProxy", type: "text" },
	{ key: "api_key", label: "Klucz API", type: "secret" },
	{ key: "classifyModel", label: "Model klasyfikacji", type: "text" },
	{ key: "draftModel", label: "Model szkiców i diagnozy", type: "text" },
	{
		key: "diagnoseEnabled",
		label: "Diagnoza AI",
		type: "bool",
		help: "AI sprawdza stanowiska i bazę apteki narzędziami IT (tylko odczyt) i zapisuje notatkę „Diagnoza AI”.",
	},
	{ key: "diagnoseMaxCalls", label: "Limit wywołań narzędzi na diagnozę", type: "number" },
	{ key: "diagnoseMaxSeconds", label: "Limit czasu diagnozy [s]", type: "number" },
	{ key: "hindsightEnabled", label: "Hindsight włączony", type: "bool" },
	{ key: "hindsightUrl", label: "Adres Hindsight", type: "text" },
	{ key: "hindsightBank", label: "Bank Hindsight", type: "text" },
	{ key: "token", label: "Token Hindsight", type: "secret" },
];

const OTRS_FIELDS: Field<OtrsSettings>[] = [
	{ key: "sshHost", label: "Host SSH", type: "text" },
	{ key: "sshPort", label: "Port SSH", type: "number" },
	{ key: "sshUser", label: "Użytkownik SSH", type: "text" },
	{ key: "private_key", label: "Klucz prywatny", type: "secret-multiline" },
	{ key: "passphrase", label: "Hasło klucza", type: "secret" },
	{
		key: "hostKeyFingerprint",
		label: "Odcisk klucza hosta",
		type: "text",
		help: "SHA256:… — bez niego połączenie jest odrzucane.",
	},
	{
		key: "years",
		label: "Historia [lata]",
		type: "number",
		help: "Plus wszystkie wciąż otwarte zgłoszenia.",
	},
	{ key: "pauseMs", label: "Pauza między partiami [ms]", type: "number" },
	{ key: "batchSize", label: "Wielkość partii", type: "number" },
];

const REPORT_FIELDS: Field<ReportSettings>[] = [
	{
		key: "enabled",
		label: "Codzienny raport niezakończonych zgłoszeń",
		type: "bool",
		help: "Każdy pracownik helpdesku dostaje mailem swoje otwarte zgłoszenia, najstarsze pierwsze. Kto nie ma przypisanych, nie dostaje maila.",
	},
	{
		key: "time",
		label: "Godzina wysyłki",
		type: "time",
		help: "Dni robocze (pon.–pt.), czas polski. Po restarcie serwera raport może wyjść do 3 h później, nigdy drugi raz tego dnia.",
	},
];

const isSecret = (t: FieldType) => t === "secret" || t === "secret-multiline";

/**
 * Formularz ustawień: wartości z serwera (sekrety już zamaskowane), sekrety
 * zawsze puste w polu hasła — puste przy zapisie = bez zmian (backend zachowuje).
 */
export function SettingsForm<T extends object>({
	section,
	fields,
}: {
	section: SettingsSection;
	fields: Field<T>[];
}) {
	const key = useHelpdeskKey();
	const query = useQuery({
		queryKey: key("settings", section),
		queryFn: () => helpdeskApi.settings<T>(section),
	});
	const [values, setValues] = useState<T | null>(null);
	useEffect(() => {
		if (!query.data) return;
		const next = { ...query.data } as Record<string, unknown>;
		for (const f of fields) if (isSecret(f.type)) next[f.key] = "";
		setValues(next as T);
	}, [query.data, fields]);
	// Wartości jako argument: opcje mutacji odświeżają się dopiero w efekcie po renderze.
	const save = useHelpdeskMutation((body: object) => helpdeskApi.saveSettings(section, body), {
		success: "Zapisano ustawienia",
		error: "Nie udało się zapisać ustawień",
	});
	const [enabling, setEnabling] = useState(false);
	if (query.isLoading || !values)
		return <p className="text-xs text-muted-foreground">Wczytywanie…</p>;
	if (query.isError) return <NoticeBanner tone="error" title="Nie udało się pobrać ustawień" />;
	const v = values as Record<string, unknown>;
	const stored = query.data as Record<string, unknown>;
	const set = (k: string, value: unknown) => setValues({ ...(values as object), [k]: value } as T);
	// Włączenie skrzynki najpierw pyta o kolejkę (wysłać czy anulować) — po zapisie nie da się tego cofnąć.
	const turnsMailboxOn = section === "mailbox" && !!v.enabled && !stored.enabled;

	return (
		<div className="max-w-3xl space-y-3">
			<div className="grid gap-x-4 gap-y-2 rounded-md border border-border bg-card p-3 md:grid-cols-2">
				{fields.map((f) => {
					const id = `hd-${section}-${f.key}`;
					const hasStored =
						isSecret(f.type) && typeof stored[f.key] === "string" && !!stored[f.key];
					return (
						<div
							key={f.key}
							className={cn(
								"space-y-1",
								(f.type === "secret-multiline" || f.type === "bool") && "md:col-span-2",
							)}
						>
							{f.type === "bool" ? (
								<label className="flex items-center gap-2 text-sm font-medium" htmlFor={id}>
									<input
										id={id}
										type="checkbox"
										checked={!!v[f.key]}
										onChange={(e) => set(f.key, e.target.checked)}
									/>
									{f.label}
								</label>
							) : (
								<label htmlFor={id} className="text-xs font-medium">
									{f.label}
								</label>
							)}
							{f.type === "text" || f.type === "time" ? (
								<Input
									id={id}
									type={f.type}
									value={String(v[f.key] ?? "")}
									onChange={(e) => set(f.key, e.target.value)}
									className={cn("h-8 text-sm", f.type === "time" && "w-32 tabular-nums")}
								/>
							) : f.type === "number" ? (
								<Input
									id={id}
									type="number"
									value={String(v[f.key] ?? "")}
									onChange={(e) => set(f.key, e.target.value === "" ? "" : Number(e.target.value))}
									className="h-8 text-sm tabular-nums"
								/>
							) : f.type === "select" ? (
								<select
									id={id}
									value={String(v[f.key] ?? "")}
									onChange={(e) => set(f.key, e.target.value)}
									className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
								>
									{f.options?.map((o) => (
										<option key={o.value} value={o.value}>
											{o.label}
										</option>
									))}
								</select>
							) : f.type === "secret" ? (
								<Input
									id={id}
									type="password"
									autoComplete="new-password"
									value={String(v[f.key] ?? "")}
									onChange={(e) => set(f.key, e.target.value)}
									placeholder={hasStored ? "zapisane — puste pole = bez zmian" : "nie ustawiono"}
									className="h-8 text-sm"
								/>
							) : f.type === "secret-multiline" ? (
								<textarea
									id={id}
									autoComplete="off"
									spellCheck={false}
									value={String(v[f.key] ?? "")}
									onChange={(e) => set(f.key, e.target.value)}
									placeholder={
										hasStored
											? "zapisany (ukryty) — wklej nowy, by zmienić"
											: "wklej klucz OpenSSH/PEM"
									}
									// Pole hasła gubi znaki nowej linii klucza — maskujemy textarea.
									style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
									className="min-h-24 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
								/>
							) : null}
							{f.help ? <p className="text-[11px] text-muted-foreground">{f.help}</p> : null}
						</div>
					);
				})}
			</div>
			<div className="flex justify-end">
				<Button
					disabled={save.isPending}
					onClick={() => (turnsMailboxOn ? setEnabling(true) : save.mutate(values))}
				>
					{save.isPending ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
					Zapisz
				</Button>
			</div>
			{turnsMailboxOn ? (
				<EnableMailboxDialog
					open={enabling}
					pending={save.isPending}
					onEnable={(cancelQueued) =>
						save.mutate({ ...values, cancelQueued }, { onSuccess: () => setEnabling(false) })
					}
					onKeepOff={() => {
						set("enabled", false);
						setEnabling(false);
					}}
				/>
			) : null}
		</div>
	);
}

/**
 * Okno włączania skrzynki: wszystko, co zebrało się w kolejce przy wyłączonej
 * skrzynce, wyszłoby od razu — pracownik decyduje, czy to wysłać, czy anulować
 * (`cancelQueued`: backend anuluje w tej samej transakcji, przed `enabled = true`).
 */
function EnableMailboxDialog({
	open,
	pending,
	onEnable,
	onKeepOff,
}: {
	open: boolean;
	pending: boolean;
	onEnable: (cancelQueued: boolean) => void;
	onKeepOff: () => void;
}) {
	const key = useHelpdeskKey();
	const outbox = useQuery({
		queryKey: key("mailbox-outbox"),
		queryFn: helpdeskApi.mailboxOutbox,
		enabled: open,
		staleTime: 0,
	});
	const total = outbox.data?.total ?? 0;
	const items = outbox.data?.items ?? [];
	// Także odświeżanie w tle: lista z cache mogła się zmienić od poprzedniego otwarcia.
	const busy = pending || outbox.isFetching || outbox.isError;
	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next && !pending) onKeepOff();
			}}
		>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Włączyć odbiór i wysyłkę poczty?</DialogTitle>
					<DialogDescription>
						Maile z datą sprzed włączenia trafią do helpdesku bez automatycznego potwierdzenia
						„przyjęte”.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-2 text-sm">
					{outbox.isLoading ? (
						<p className="text-muted-foreground">Sprawdzam kolejkę wysyłki…</p>
					) : outbox.isError ? (
						<NoticeBanner tone="error" title="Nie udało się sprawdzić kolejki wysyłki" />
					) : total ? (
						<>
							<p className="font-medium">
								Niewysłane wiadomości w kolejce: {total} — po włączeniu wyjdą od razu.
							</p>
							<ul className="max-h-60 divide-y divide-border overflow-y-auto rounded-md border border-border text-xs">
								{items.map((m) => (
									<li key={m.articleId} className="flex items-center gap-2 px-2 py-1">
										<span className="shrink-0 font-mono">#{m.number}</span>
										<span className="shrink-0 text-muted-foreground">
											{m.auto ? "szablon" : "odpowiedź agenta"}
										</span>
										<span className="min-w-0 flex-1 truncate" title={m.subject ?? undefined}>
											{m.toAddrs.join(", ")}
										</span>
										<span className="shrink-0 tabular-nums text-muted-foreground">
											{formatDateTime(m.createdAt)}
										</span>
									</li>
								))}
							</ul>
							{total > items.length ? (
								<p className="text-xs text-muted-foreground">
									Pokazano {items.length} najstarszych z {total}.
								</p>
							) : null}
						</>
					) : (
						<p className="text-muted-foreground">Kolejka wysyłki jest pusta.</p>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" disabled={pending} onClick={onKeepOff}>
						Nie włączaj
					</Button>
					{total ? (
						<>
							<Button variant="outline" disabled={busy} onClick={() => onEnable(true)}>
								Anuluj wszystkie i włącz
							</Button>
							<Button disabled={busy} onClick={() => onEnable(false)}>
								Wyślij
							</Button>
						</>
					) : (
						<Button disabled={busy} onClick={() => onEnable(false)}>
							Włącz
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function TestResult({ ok, label, error }: { ok: boolean; label: string; error?: string }) {
	return (
		<p
			className={cn(
				"flex items-center gap-1.5 text-xs",
				ok ? "text-status-success-text" : "text-status-error-text",
			)}
		>
			{ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
			{label}: {ok ? "OK" : (error ?? "błąd")}
		</p>
	);
}

function MailboxStatusPanel() {
	const key = useHelpdeskKey();
	const { data } = useQuery({
		queryKey: key("mailbox-status"),
		queryFn: helpdeskApi.mailboxStatus,
		refetchInterval: 60_000,
	});
	const test = useHelpdeskMutation(helpdeskApi.testMailbox, { error: "Test nie powiódł się" });
	return (
		<div className="space-y-2 rounded-md border border-border bg-card p-3 text-xs">
			{data ? (
				<>
					{!data.enabled ? (
						<NoticeBanner
							tone="info"
							title="Odbiór poczty wyłączony."
							description="Farmaster nie pobiera ani nie wysyła maili helpdesku — OTRS działa bez zmian. Włącz dopiero przy przejściu z OTRS."
						/>
					) : null}
					<p>
						Ostatni odbiór: {formatDateTime(data.lastPollAt)} · ostatni udany:{" "}
						{formatDateTime(data.lastOkAt)}
					</p>
					{data.lastError ? (
						<p className="text-status-error-text">Ostatni błąd: {data.lastError}</p>
					) : null}
					<p>
						Kolejka wysyłki: {data.outbox.queued} w kolejce
						{data.outbox.failed ? (
							<span className="text-status-error-text"> · {data.outbox.failed} z błędem</span>
						) : null}
					</p>
					{data.quota ? (
						<div className="space-y-1">
							<p className={cn(data.quota.warning && "font-medium text-status-warning-text")}>
								Zajętość skrzynki: {data.quota.percent.toFixed(0)}% (
								{Math.round(data.quota.usedKb / 1024)} / {Math.round(data.quota.limitKb / 1024)} MB)
								{data.quota.warning
									? " — skrzynka się zapełnia, skróć retencję albo włącz sprzątanie"
									: ""}
							</p>
							<div className="h-1.5 overflow-hidden rounded-full bg-muted">
								<div
									className={cn(
										"h-full",
										data.quota.warning ? "bg-status-warning-text" : "bg-primary",
									)}
									style={{ width: `${Math.min(100, data.quota.percent)}%` }}
								/>
							</div>
						</div>
					) : (
						<p className="text-muted-foreground">
							Zajętość skrzynki: brak danych (serwer bez QUOTA albo odbiór nie działał).
						</p>
					)}
				</>
			) : null}
			<div className="flex flex-wrap items-center gap-3">
				<Button
					size="sm"
					variant="outline"
					disabled={test.isPending}
					onClick={() => test.mutate(undefined)}
				>
					{test.isPending ? (
						<Loader2 className="mr-1.5 size-4 animate-spin" />
					) : (
						<PlugZap className="mr-1.5 size-4" />
					)}
					Test połączenia
				</Button>
				<span className="text-muted-foreground">
					sprawdza zapisane ustawienia, bez wysyłania maila
				</span>
			</div>
			{test.data ? (
				<div className="space-y-0.5">
					<TestResult ok={test.data.imap.ok} label="IMAP" error={test.data.imap.error} />
					<TestResult ok={test.data.smtp.ok} label="SMTP" error={test.data.smtp.error} />
				</div>
			) : null}
		</div>
	);
}

/** Wiadomości `failed` (także Robaczka): link do miejsca w czacie i „Wyślij ponownie”. Cel paska w skrzynce. */
function UnsentMailPanel() {
	const key = useHelpdeskKey();
	const { data } = useQuery({
		queryKey: key("mailbox-failed"),
		queryFn: helpdeskApi.mailboxFailed,
		refetchInterval: 60_000,
	});
	if (!data?.total) return null;
	return (
		<div className="space-y-1 rounded-md border border-border bg-card p-3 text-xs">
			<p className="font-medium text-status-error-text">
				Nie udało się wysłać: {data.total}
				{data.total > data.items.length ? ` (pokazano ${data.items.length} najnowszych)` : ""}
			</p>
			<ul className="max-h-72 divide-y divide-border overflow-y-auto">
				{data.items.map((m) => (
					<li key={m.articleId} className="flex items-center gap-2 whitespace-nowrap py-1">
						<Link
							to="/helpdesk/czat"
							search={chatSearch({
								idfirm: m.idfirm,
								email: m.contactEmail,
								ticket: m.number,
								article: m.articleId,
							})}
							className="shrink-0 font-mono text-primary hover:underline"
						>
							#{m.number}
						</Link>
						<span className="shrink-0 text-muted-foreground">
							{m.auto ? "szablon" : "odpowiedź agenta"}
						</span>
						<span className="shrink-0">{m.toAddrs.join(", ")}</span>
						<span
							className="min-w-0 flex-1 truncate text-muted-foreground"
							title={m.deliveryError ?? undefined}
						>
							{m.deliveryError}
						</span>
						<span className="shrink-0 tabular-nums text-muted-foreground">
							{formatDateTime(m.createdAt)}
						</span>
						<ResendButton articleId={m.articleId} />
					</li>
				))}
			</ul>
		</div>
	);
}

function AiTestPanel() {
	const test = useHelpdeskMutation(helpdeskApi.testAi, { error: "Test nie powiódł się" });
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card p-3 text-xs">
			<Button
				size="sm"
				variant="outline"
				disabled={test.isPending}
				onClick={() => test.mutate(undefined)}
			>
				{test.isPending ? (
					<Loader2 className="mr-1.5 size-4 animate-spin" />
				) : (
					<PlugZap className="mr-1.5 size-4" />
				)}
				Test AI
			</Button>
			{test.data ? (
				<TestResult
					ok={test.data.ok}
					label={`${test.data.model} (${test.data.durationMs} ms)`}
					error={test.data.error}
				/>
			) : (
				<span className="text-muted-foreground">
					sprawdza zapisany adres CLIProxy, klucz i model
				</span>
			)}
		</div>
	);
}

export function SettingsPage({
	tab,
	onTabChange,
}: {
	tab: SettingsTab;
	onTabChange: (tab: SettingsTab) => void;
}) {
	return (
		<div className="flex h-full flex-col gap-3 overflow-y-auto p-3 sm:p-4">
			<PageHeader
				title="Helpdesk — konfiguracja"
				description="Skrzynka helpdesk@, AI, połączenie z OTRS, alert SMS i raport dzienny. Sekrety są szyfrowane i nie wracają do przeglądarki."
			/>
			<Tabs value={tab} onValueChange={(v) => onTabChange(v as SettingsTab)}>
				<TabsList>
					<TabsTrigger value="skrzynka">Skrzynka</TabsTrigger>
					<TabsTrigger value="ai">AI i Hindsight</TabsTrigger>
					<TabsTrigger value="otrs">OTRS</TabsTrigger>
					<TabsTrigger value="spam">Spam i blokady</TabsTrigger>
					<TabsTrigger value="sms">Alert SMS</TabsTrigger>
					<TabsTrigger value="raport">Raport dzienny</TabsTrigger>
				</TabsList>
				<TabsContent value="skrzynka" className="space-y-3">
					<MailboxStatusPanel />
					<UnsentMailPanel />
					<SettingsForm<MailboxSettings> section="mailbox" fields={MAILBOX_FIELDS} />
				</TabsContent>
				<TabsContent value="ai" className="space-y-3">
					<AiTestPanel />
					<SettingsForm<AiSettings> section="ai" fields={AI_FIELDS} />
				</TabsContent>
				<TabsContent value="otrs" className="space-y-3">
					<NoticeBanner
						tone="info"
						title="Połączenie tylko do importu historii."
						description="Eksport idzie przez SSH partiami z pauzą — serwer OTRS jest współdzielony z inną aplikacją."
					/>
					<SettingsForm<OtrsSettings> section="otrs" fields={OTRS_FIELDS} />
				</TabsContent>
				<TabsContent value="spam" className="space-y-3">
					<BlockRulesPanel />
				</TabsContent>
				<TabsContent value="sms" className="space-y-3">
					<AlertsPanel />
				</TabsContent>
				<TabsContent value="raport" className="space-y-3">
					<ReportSubscription />
					<SettingsForm<ReportSettings> section="report" fields={REPORT_FIELDS} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
